import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { C } from '../albumColors';
import {
  codeToNumber,
  getPrefixInfo,
  searchPrefixes,
  splitCodeParts,
  parseCodeList,
} from '../utils/quickAddParser';
import { stickerCode } from '../albumData';
import { COLOR_BY_CODE } from '../albumDecorations';

// Slot 20 = FWC00 (portada). Ver albumData.js#stickerParts.
const FWC_COVER_SLOT = 20;

const STATE_FALTANTE = 'missing';
const STATE_OWNED = 'owned';
const STATE_REPE = 'repe';

function getStickerState(n, ownedSet, repsMap, selected) {
  const isSelected = selected.has(n);
  const owned = ownedSet.has(n);
  const reps = repsMap.get(n) || 0;
  return {
    state: !owned ? STATE_FALTANTE : reps >= 1 ? STATE_REPE : STATE_OWNED,
    selected: isSelected,
  };
}

/**
 * Barra de búsqueda inline para agregar figuritas a la selección
 * tipeando códigos (`ARG11`) o nombres de país (`arg`).
 *
 * Vive embebida en el AlbumActionBar y siempre está visible. El dropdown
 * sale flotando debajo del input cuando hay sugerencias.
 */
function QuickAddBar({
  stickers,
  selected,
  dispatch,
  theme,
  chrome,
  onAdded,
  // Modo navegar-a-país (mobile /album/5): tipear sólo el prefijo / nombre
  // del país y Enter scrollea a la sección sin agregar nada y cierra el
  // buscador. Tipear código + número agrega y scrollea como en desktop.
  navigateOnPrefix = false,
  onNavigateToCountry,
  // Cuando es true, cualquier commit cierra el buscador (blur).
  closeOnCommit = false,
  wrapperClassName,
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const [pinnedPrefix, setPinnedPrefix] = useState(null);
  const [highlight, setHighlight] = useState(0);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);

  const accent = chrome?.accent ?? theme?.primaryAccent ?? C.primary;
  const gradient = chrome?.gradient || null;
  const shimmer = !!chrome?.shimmer;
  const useCountryChips = chrome?.chipMode === 'country';

  // Color del badge de prefijo para una sugerencia: bandera si chipMode
  // === 'country' y el prefijo corresponde a un país (no FWC/CC), o el
  // accent del chrome en cualquier otro caso.
  const colorForPrefix = (prefix) => {
    if (useCountryChips) {
      const c = COLOR_BY_CODE[prefix];
      if (c) return c;
    }
    return accent;
  };

  const ownedSet = useMemo(
    () => new Set(stickers.filter((s) => s.owned).map((s) => s.n)),
    [stickers]
  );
  const repsMap = useMemo(() => {
    const m = new Map();
    for (const s of stickers) m.set(s.n, s.reps || 0);
    return m;
  }, [stickers]);

  const suggestions = useMemo(() => {
    const trimmed = input.trim();
    if (pinnedPrefix) {
      const info = getPrefixInfo(pinnedPrefix);
      if (!info) return [];
      const numStr = trimmed.replace(/\D/g, '');
      const items = [];
      const isFwc = pinnedPrefix === 'FWC';
      // FWC: iteramos 1..19 (slots 1..19); el slot 20 (portada) se agrega aparte
      // como "FWC00" si el filtro lo deja entrar.
      const lastIdx = isFwc ? info.count - 1 : info.count;
      for (let i = 1; i <= lastIdx; i++) {
        if (numStr && !String(i).startsWith(numStr)) continue;
        items.push({
          kind: 'sticker',
          prefix: pinnedPrefix,
          n: info.from + i - 1,
          name: info.name,
        });
      }
      if (isFwc) {
        const matches = !numStr || '0'.startsWith(numStr) || '00'.startsWith(numStr);
        if (matches) {
          items.push({
            kind: 'sticker',
            prefix: 'FWC',
            n: FWC_COVER_SLOT,
            name: info.name,
          });
        }
      }
      return items.slice(0, 40);
    }
    const direct = codeToNumber(trimmed);
    if (direct != null) {
      const m = trimmed.toUpperCase().match(/^([A-Z]+)(\d+)$/);
      const info = getPrefixInfo(m[1]);
      return [
        {
          kind: 'sticker',
          prefix: m[1],
          n: direct,
          name: info?.name || m[1],
        },
      ];
    }
    const partial = splitCodeParts(trimmed);
    if (partial && partial.digits) {
      const info = getPrefixInfo(partial.prefix);
      if (info) {
        return [{
          kind: 'partial-invalid',
          prefix: partial.prefix,
          digits: partial.digits,
          name: info.name,
          count: info.count,
        }];
      }
    }
    return searchPrefixes(trimmed)
      .slice(0, 30)
      .map((m) => ({ kind: 'prefix', ...m }));
  }, [input, pinnedPrefix]);

  useEffect(() => {
    if (highlight >= suggestions.length) setHighlight(0);
  }, [suggestions.length, highlight]);

  // Cerrar el dropdown al clickear fuera.
  useEffect(() => {
    if (!focused) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setFocused(false);
    };
    window.addEventListener('mousedown', onDoc);
    return () => window.removeEventListener('mousedown', onDoc);
  }, [focused]);

  const addToSelection = (nums) => {
    if (!nums || nums.length === 0) return;
    dispatch({ type: 'ADD_TO_SELECTION', nums });
    // Aviso al padre con el último n agregado para que pueda hacer scroll.
    if (onAdded) onAdded(nums[nums.length - 1]);
  };

  const closeInput = () => {
    inputRef.current?.blur();
    setFocused(false);
  };

  const commitSelection = (idx = highlight) => {
    const sug = suggestions[idx];
    if (!sug) return false;
    if (sug.kind === 'prefix') {
      // Enter sobre un prefijo siempre navega a la sección (no pinnea).
      // El input se vacía pero el focus se mantiene en desktop para seguir
      // tipeando otro código. En mobile (closeOnCommit) cerramos el input.
      if (onNavigateToCountry) onNavigateToCountry(sug.from);
      setInput('');
      setHighlight(0);
      if (closeOnCommit) closeInput();
      return true;
    }
    if (sug.kind === 'sticker') {
      addToSelection([sug.n]);
      setInput('');
      setHighlight(0);
      if (closeOnCommit) closeInput();
      return true;
    }
    return false;
  };

  const ingestRawList = (text) => {
    const items = parseCodeList(text);
    const ns = items.filter((it) => it.n != null).map((it) => it.n);
    if (ns.length === 0) return false;
    addToSelection(ns);
    return true;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitSelection();
      return;
    }
    if (e.key === 'Tab' && !e.shiftKey) {
      const sug = suggestions[highlight];
      if (sug && sug.kind === 'prefix') {
        e.preventDefault();
        setPinnedPrefix(sug.prefix);
        setInput('');
        setHighlight(0);
        return;
      }
      if (!pinnedPrefix && input) {
        const partial = splitCodeParts(input.trim());
        if (partial && !partial.digits && getPrefixInfo(partial.prefix)) {
          e.preventDefault();
          setPinnedPrefix(partial.prefix.toUpperCase());
          setInput('');
          setHighlight(0);
          return;
        }
      }
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
      return;
    }
    if (e.key === 'Backspace' && input === '' && pinnedPrefix) {
      e.preventDefault();
      setPinnedPrefix(null);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      inputRef.current?.blur();
      setFocused(false);
    }
  };

  const handleChange = (raw) => {
    const lastSep = Math.max(raw.lastIndexOf(','), raw.lastIndexOf(' '), raw.lastIndexOf(';'));
    if (lastSep >= 0) {
      const head = raw.slice(0, lastSep);
      const tail = raw.slice(lastSep + 1);
      if (pinnedPrefix) {
        const info = getPrefixInfo(pinnedPrefix);
        const isFwc = pinnedPrefix === 'FWC';
        const tokens = head.split(/[,\s;]+/).map((s) => s.trim()).filter(Boolean);
        const nums = [];
        for (const tok of tokens) {
          const num = parseInt(tok, 10);
          if (!info || !Number.isFinite(num)) {
            if (!info) {
              const n = codeToNumber(tok);
              if (n != null) nums.push(n);
            }
            continue;
          }
          // FWC: 0/00 → portada (slot 20); 20 no existe.
          if (isFwc && num === 0) {
            nums.push(FWC_COVER_SLOT);
          } else if (isFwc && num >= 1 && num < info.count) {
            nums.push(info.from + num - 1);
          } else if (!isFwc && num >= 1 && num <= info.count) {
            nums.push(info.from + num - 1);
          }
        }
        addToSelection(nums);
      } else {
        ingestRawList(head);
      }
      setInput(tail);
      return;
    }
    setInput(raw);
  };

  const dropdownOpen = focused && input.length > 0 && suggestions.length > 0;

  // Para gradient mode: el border es el gradient (vía background-image en
  // un wrapper) y el interior es blanco con un padding de 1px que simula border.
  const wrapStyle = gradient
    ? {
        background: gradient,
        backgroundSize: shimmer ? '200% 100%' : '100% 100%',
        padding: focused ? 2 : 1,
        boxShadow: focused ? `0 0 0 3px ${accent}33` : 'none',
        transition: 'box-shadow 150ms ease, padding 150ms ease',
      }
    : {
        backgroundColor: focused ? `${accent}14` : `${accent}0d`,
        border: `1px solid ${focused ? accent : `${accent}33`}`,
        boxShadow: focused ? `0 0 0 3px ${accent}33` : 'none',
      };

  const innerStyle = gradient
    ? {
        backgroundColor: focused ? '#fffbe9' : '#fffdf3',
        borderRadius: 7,
      }
    : {};

  return (
    <div ref={wrapRef} className={wrapperClassName ?? 'relative shrink-0 w-[200px] desktop-xl:w-[260px]'}>
      <div
        className={`rounded-lg transition-all ${gradient && shimmer ? 'cm-chrome-shimmer' : ''}`}
        style={wrapStyle}
      >
      <div
        className="flex items-center gap-1.5 pl-2.5 pr-2 py-1.5"
        style={innerStyle}
      >
        <span className="material-symbols-outlined text-[16px]" style={{ color: accent, opacity: focused ? 1 : 0.85 }}>bolt</span>
        {pinnedPrefix && (() => {
          const pinColor = colorForPrefix(pinnedPrefix);
          return (
          <span
            className="flex items-center gap-0.5 px-1.5 py-0 rounded text-[10px] font-bold tabular-nums"
            style={{ backgroundColor: `${pinColor}33`, color: pinColor }}
            title={t('Quitar país')}
          >
            {pinnedPrefix}
            <button
              type="button"
              onClick={() => setPinnedPrefix(null)}
              className="ml-0.5 hover:opacity-70"
              aria-label={t('Quitar país')}
            >
              <span className="material-symbols-outlined text-[12px]">close</span>
            </button>
          </span>
          );
        })()}
        <input
          ref={inputRef}
          type="text"
          value={input}
          onFocus={() => setFocused(true)}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            pinnedPrefix
              ? pinnedPrefix === 'FWC'
                ? t('Número 1-19 o 00')
                : t('Número 1-{{n}}', { n: getPrefixInfo(pinnedPrefix)?.count || 20 })
              : t('Buscar: ARG11 / arg')
          }
          className="cm-quickadd-input flex-1 min-w-0 text-[12px] font-bold tabular-nums placeholder:font-medium"
          style={{ color: accent }}
          autoComplete="off"
          spellCheck={false}
          aria-label={t('Agregar figuritas por código')}
        />
        <style>{`
          .cm-quickadd-input,
          .cm-quickadd-input:focus,
          .cm-quickadd-input:focus-visible,
          .cm-quickadd-input:active {
            background: transparent !important;
            border: 0 !important;
            outline: 0 !important;
            box-shadow: none !important;
            appearance: none !important;
            -webkit-appearance: none !important;
            padding: 0 !important;
          }
        `}</style>
      </div>
      </div>

      {dropdownOpen && (
        <div
          className="absolute left-0 right-0 mt-1 rounded-lg border shadow-lg overflow-hidden z-30 max-h-[280px] overflow-y-auto"
          style={{
            backgroundColor: 'var(--md-sys-color-surface, #ffffff)',
            borderColor: 'var(--md-sys-color-outline-variant, rgba(0,0,0,0.18))',
            minWidth: 280,
          }}
        >
          {suggestions.map((sug, idx) => {
            const active = idx === highlight;
            if (sug.kind === 'prefix') {
              const badgeColor = colorForPrefix(sug.prefix);
              return (
                <button
                  key={`p-${sug.prefix}`}
                  type="button"
                  onMouseEnter={() => setHighlight(idx)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => commitSelection(idx)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left transition-colors"
                  style={{ backgroundColor: active ? `${accent}18` : 'transparent' }}
                >
                  <span className="flex items-center gap-2">
                    <span
                      className="px-1.5 py-0.5 rounded text-[11px] font-bold tabular-nums"
                      style={{ backgroundColor: `${badgeColor}26`, color: badgeColor }}
                    >
                      {sug.prefix}
                    </span>
                    <span className="text-sm font-medium text-on-surface">{sug.name}</span>
                  </span>
                  <span className="text-[10px] text-on-surface-variant">
                    {sug.sectionLabel} · {sug.count} {t('figus')}
                  </span>
                </button>
              );
            }
            if (sug.kind === 'partial-invalid') {
              const isFwc = sug.prefix === 'FWC';
              return (
                <div key="partial-invalid" className="px-3 py-2 text-[12px] text-on-surface-variant">
                  <span className="font-bold" style={{ color: C.error }}>{sug.prefix}{sug.digits}</span>{' '}
                  {isFwc
                    ? t('— FWC va de 1 a 19, o 00 para la portada')
                    : t('— {{name}} sólo va de 1 a {{n}}', { name: sug.name, n: sug.count })}
                </div>
              );
            }
            const info = getStickerState(sug.n, ownedSet, repsMap, selected);
            const stIcon = info.selected
              ? { icon: 'check_box', color: accent, label: t('seleccionada'), addable: false }
              : info.state === STATE_FALTANTE
                ? { icon: 'add_circle', color: accent, label: t('te falta'), addable: true }
                : info.state === STATE_OWNED
                  ? { icon: 'check_circle', color: '#16a34a', label: t('ya tenés'), addable: true }
                  : { icon: 'add_circle', color: '#f59e0b', label: t('+1 sería repetida'), addable: true };
            const badgeColor = colorForPrefix(sug.prefix);
            const kbdStyle = {
              backgroundColor: 'rgba(0,0,0,0.05)',
              border: '1px solid rgba(0,0,0,0.12)',
            };
            return (
              <button
                key={`s-${sug.n}-${idx}`}
                type="button"
                onMouseEnter={() => setHighlight(idx)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => commitSelection(idx)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left transition-colors"
                style={{ backgroundColor: active ? `${accent}18` : 'transparent' }}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span
                    className="px-1.5 py-0.5 rounded text-[11px] font-bold tabular-nums shrink-0"
                    style={{ backgroundColor: `${badgeColor}26`, color: badgeColor }}
                  >
                    {stickerCode(sug.n)}
                  </span>
                  <span className="text-[11px] text-on-surface-variant truncate">{sug.name}</span>
                </span>
                <span className="flex items-center gap-1.5 shrink-0">
                  {/* Mantengo el ícono de estado (te falta / ya tenés / repe)
                      como señal visual chica, pero reemplazo el label de
                      texto por el hint de selección rápida con las 3 teclas
                      que la disparan: coma, espacio, enter. */}
                  <span
                    className="material-symbols-outlined text-[16px] shrink-0"
                    style={{ color: stIcon.color }}
                  >
                    {stIcon.icon}
                  </span>
                  {stIcon.addable ? (
                    <span className="flex items-center gap-0.5 text-[10px] text-on-surface-variant opacity-80">
                      <kbd className="px-1 rounded text-[9px] font-bold tabular-nums leading-none py-0.5" style={kbdStyle}>,</kbd>
                      <kbd className="px-1 rounded text-[9px] font-bold leading-none py-0.5" style={kbdStyle}>␣</kbd>
                      <kbd className="px-1 rounded text-[9px] font-bold leading-none py-0.5" style={kbdStyle}>↵</kbd>
                      <span className="ml-1">{t('para seleccionar rápido')}</span>
                    </span>
                  ) : (
                    <span className="text-[11px]" style={{ color: stIcon.color }}>{stIcon.label}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default QuickAddBar;
